import type { AutopilotRun } from '@/api/autopilotApi'
import type {
  ConfirmSkeletonInput,
  ProposeSkeletonInput,
  SkeletonJobStatus,
  SkeletonJobSubmitResponse,
  SkeletonLayout,
  VolumeSkeleton,
} from '@/api/skeletonApi'
import type { Project } from '@/types/nf'

export type AutopilotCreationStage = 'create_project' | 'propose_skeleton' | 'confirm_skeleton' | 'confirm_outlines' | 'start_autopilot'

export interface AutopilotCreationInput {
  targetWords: number
  chapterWordTarget: number
  branchId: string
}

export interface AutopilotCreationProposal {
  layout: SkeletonLayout
  volumes: VolumeSkeleton[]
}

export interface AutopilotCreationResume {
  project?: Project
  proposal?: AutopilotCreationProposal
  skeletonConfirmed?: boolean
  outlinesConfirmed?: boolean
}

export interface AutopilotCreationDependencies {
  createProject: () => Promise<Project>
  proposeSkeleton: (projectId: string, body: ProposeSkeletonInput) => Promise<SkeletonJobSubmitResponse>
  waitForSkeletonJob: (projectId: string, jobId: string) => Promise<SkeletonJobStatus>
  confirmSkeleton: (projectId: string, body: ConfirmSkeletonInput) => Promise<SkeletonJobSubmitResponse>
  confirmOutline: (projectId: string, chapterNo: number, branchId?: string) => Promise<unknown>
  startAutopilot: (projectId: string, body: { scope: 'volume'; target_volume_no: 1; branch_id: string }) => Promise<AutopilotRun>
}

export interface AutopilotCreationResult {
  project: Project
  run: AutopilotRun
}

export class AutopilotCreationError extends Error {
  readonly stage: AutopilotCreationStage
  readonly resume: AutopilotCreationResume
  readonly cause: unknown

  constructor(stage: AutopilotCreationStage, resume: AutopilotCreationResume, cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause))
    this.name = 'AutopilotCreationError'
    this.stage = stage
    this.resume = { ...resume }
    this.cause = cause
  }
}

export async function runAutopilotCreation(
  input: AutopilotCreationInput,
  dependencies: AutopilotCreationDependencies,
  prior: AutopilotCreationResume = {},
  onStage?: (stage: AutopilotCreationStage) => void,
): Promise<AutopilotCreationResult> {
  const resume: AutopilotCreationResume = { ...prior }

  if (!resume.project) {
    onStage?.('create_project')
    try {
      resume.project = await dependencies.createProject()
    } catch (error) {
      throw new AutopilotCreationError('create_project', resume, error)
    }
  }

  if (!resume.proposal) {
    onStage?.('propose_skeleton')
    try {
      const submitted = await dependencies.proposeSkeleton(resume.project.id, {
        target_words: input.targetWords,
        chapter_word_target: input.chapterWordTarget,
      })
      const completed = await dependencies.waitForSkeletonJob(resume.project.id, submitted.job_id)
      if (!completed.result?.layout || !completed.result.volumes?.length) {
        throw new Error('skeleton proposal completed without layout and volumes')
      }
      resume.proposal = {
        layout: completed.result.layout,
        volumes: completed.result.volumes,
      }
    } catch (error) {
      throw new AutopilotCreationError('propose_skeleton', resume, error)
    }
  }

  if (!resume.skeletonConfirmed) {
    onStage?.('confirm_skeleton')
    try {
      const submitted = await dependencies.confirmSkeleton(resume.project.id, {
        chapter_word_target: input.chapterWordTarget,
        volumes: resume.proposal.volumes,
      })
      await dependencies.waitForSkeletonJob(resume.project.id, submitted.job_id)
      resume.skeletonConfirmed = true
    } catch (error) {
      throw new AutopilotCreationError('confirm_skeleton', resume, error)
    }
  }

  // 🔴 skeleton/confirm 只确认【骨架】, 它写入的逐章大纲状态是 draft;
  // 而 Autopilot 要求 confirmed —— 不补这一步会直接吃 422 autopilot_plan_not_ready。
  // 端到端实测发现: 契约 v2 的四步流程漏了这一环。
  if (!resume.outlinesConfirmed) {
    onStage?.('confirm_outlines')
    try {
      const firstVolume = resume.proposal.volumes[0]
      if (!firstVolume) {
        throw new Error('skeleton proposal has no volumes')
      }
      for (let chapterNo = firstVolume.start_chapter_no; chapterNo <= firstVolume.end_chapter_no; chapterNo += 1) {
        await dependencies.confirmOutline(resume.project.id, chapterNo, input.branchId)
      }
      resume.outlinesConfirmed = true
    } catch (error) {
      throw new AutopilotCreationError('confirm_outlines', resume, error)
    }
  }

  onStage?.('start_autopilot')
  try {
    const run = await dependencies.startAutopilot(resume.project.id, {
      scope: 'volume',
      target_volume_no: 1,
      branch_id: input.branchId,
    })
    return { project: resume.project, run }
  } catch (error) {
    throw new AutopilotCreationError('start_autopilot', resume, error)
  }
}
